/**
 * BurstMode — scan en rafale ASYNCHRONE. On peut photographier 10+ bouteilles d'affilée
 * sans attendre : chaque photo entre en file (persistée dans IndexedDB) puis est analysée
 * en arrière-plan, 3 en parallèle. Statuts : Envoi → Analyse → Prêt / À vérifier / Erreur.
 * Un badge de progression est diffusé à la navigation. Validation groupée (une seule annulation),
 * et les photos sont enregistrées sur la cuvée créée/rapprochée.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, CheckCircle2, AlertTriangle, XCircle, Send } from 'lucide-react';
import { scannerApi, caveApi } from '../../services/api';
import { useUndoToast } from '../../hooks/useUndoToast';
import { Button, EmptyState } from '../../components/ui/kit';
import { QuantityStepper } from '../../components/ui/QuantityStepper';
import { WineColorDot } from '../../components/wine/wine';
import { scanPut, scanAll, scanClear } from '../../offline/idb';
import { invalidateStock } from '../../services/refresh';
import CameraView from './CameraView';

const MAX_CONCURRENT = 3;
let _seq = 0;

const STATUS_META = {
  queued: { label: 'Envoi', cls: 'text-n600', Icon: Send },
  analyzing: { label: 'Analyse', cls: 'text-accent-700', Icon: Loader2, spin: true },
  ready: { label: 'Prêt', cls: 'text-green-700', Icon: CheckCircle2 },
  toverify: { label: 'À vérifier', cls: 'text-amber-700', Icon: AlertTriangle },
  error: { label: 'Erreur', cls: 'text-red-700', Icon: XCircle },
};

function emitBadge(count) {
  window.dispatchEvent(new CustomEvent('scan-queue', { detail: { active: count } }));
}

export default function BurstMode({ onExit }) {
  const qc = useQueryClient();
  const { notify } = useUndoToast();
  const [items, setItems] = useState([]);
  const itemsRef = useRef([]);
  const activeRef = useRef(0);

  const sync = useCallback((next) => {
    itemsRef.current = next;
    setItems(next);
    const inflight = next.filter((it) => it.status === 'queued' || it.status === 'analyzing').length;
    emitBadge(inflight);
  }, []);

  // Reprise : recharge la file persistée au montage (photos non encore validées).
  useEffect(() => {
    scanAll().then((saved) => {
      if (saved.length) {
        // Les analyses interrompues repassent en file.
        const restored = saved.map((s) => (s.status === 'analyzing' ? { ...s, status: 'queued' } : s));
        sync(restored);
        setTimeout(pump, 0);
      }
    });
    return () => emitBadge(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patch = useCallback((id, changes) => {
    const next = itemsRef.current.map((it) => (it.id === id ? { ...it, ...changes } : it));
    sync(next);
    const updated = next.find((it) => it.id === id);
    if (updated) scanPut({ ...updated, b64: updated.b64, preview: updated.preview });
  }, [sync]);

  const analyze = useCallback(async (item) => {
    activeRef.current += 1;
    patch(item.id, { status: 'analyzing' });
    try {
      const res = await scannerApi.analyzeLabel([item.b64], false);
      const ex = res.extraction || {};
      const top = res.candidates?.[0];
      const strong = top && top.score >= 70;
      const data = {
        cuvee_id: strong ? top.cuvee_id : null,
        name: top?.name || ex.cuvee || 'Étiquette illisible',
        year: ex.millesime || 'NV', format: ex.format || '750',
        color: top?.color || ex.couleur || 'rouge',
        domaine: top?.domain_name || ex.domaine || '', appellation: ex.appellation || '',
        cepages: ex.cepages || [], degre: ex.degre || '',
      };
      const status = !ex.cuvee && !strong ? 'error' : strong ? 'ready' : 'toverify';
      patch(item.id, { status, data, error: status === 'error' ? 'Étiquette illisible' : null });
    } catch (err) {
      patch(item.id, { status: 'error', error: err.message || 'Analyse impossible' });
    } finally {
      activeRef.current -= 1;
      setTimeout(pump, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patch]);

  // Démarre autant d'analyses que possible (max 3 simultanées).
  const pump = useCallback(() => {
    while (activeRef.current < MAX_CONCURRENT) {
      const nextItem = itemsRef.current.find((it) => it.status === 'queued');
      if (!nextItem) break;
      // Marque immédiatement pour éviter de le relancer dans la même boucle.
      itemsRef.current = itemsRef.current.map((it) => (it.id === nextItem.id ? { ...it, status: 'analyzing' } : it));
      analyze(nextItem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyze]);

  const onCapture = (b64, preview) => {
    const item = { id: `scan-${Date.now()}-${_seq++}`, b64, preview, status: 'queued', qty: 1, data: null, error: null };
    const next = [...itemsRef.current, item];
    sync(next);
    scanPut(item);
    setTimeout(pump, 0);
  };

  const retry = (id) => { patch(id, { status: 'queued', error: null }); setTimeout(pump, 0); };
  const remove = (id) => sync(itemsRef.current.filter((it) => it.id !== id));

  const validatable = items.filter((it) => it.status === 'ready' || it.status === 'toverify');
  const inflight = items.filter((it) => it.status === 'queued' || it.status === 'analyzing').length;
  const totalBottles = validatable.reduce((s, it) => s + it.qty, 0);

  const validate = async () => {
    const chosen = validatable;
    const payloads = chosen.map((it) =>
      it.data.cuvee_id
        ? { cuvee_id: it.data.cuvee_id, year: it.data.year, format: it.data.format, quantity: it.qty }
        : { new_cuvee: { name: it.data.name, color: it.data.color, appellation: it.data.appellation, cepages: it.data.cepages, degre: it.data.degre },
            new_domain: { name: it.data.domaine || 'Domaine inconnu' }, year: it.data.year, format: it.data.format, quantity: it.qty }
    );
    const res = await scannerApi.batch(payloads);
    // Enregistre chaque photo sur la cuvée créée / rapprochée (best-effort).
    await Promise.allSettled((res.results || []).map(async (r, i) => {
      const it = chosen[i];
      if (!r.cuvee_id || !it?.preview) return;
      try {
        const blob = await (await fetch(it.preview)).blob();
        const file = new File([blob], 'etiquette.jpg', { type: blob.type || 'image/jpeg' });
        await caveApi.uploadImage(r.cuvee_id, 'label', file);
      } catch { /* photo non bloquante */ }
    }));
    invalidateStock(qc);
    notify(`${res.total_bottles} bouteilles ajoutées (${res.count} références)`,
      () => scannerApi.undo(res.group_id).then(() => invalidateStock(qc)));
    await scanClear();
    sync([]);
    onExit();
  };

  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="burst-mode">
      <div>
        <CameraView burstActive burstCount={items.length} captureLabel="Ajouter la bouteille" onResult={onCapture} />
        <p className="text-[13px] text-n600 mt-2" data-testid="burst-inflight">
          {inflight > 0 ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {inflight} en cours de traitement…</span>
            : 'Prêt — photographiez les bouteilles à la suite, sans attendre.'}
        </p>
      </div>
      <div>
        <div className="flex items-baseline justify-between mb-2 hair-b pb-2">
          <h3 className="font-title text-2xl">File de scan</h3>
          <span className="text-[13px] text-n500 nums">{items.length} photo(s) · {totalBottles} btl prête(s)</span>
        </div>
        {items.length === 0 ? (
          <EmptyState title="Aucune photo" description="Les photos s'analysent en arrière-plan, 3 à la fois." testId="burst-empty" />
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-auto pr-1">
            {items.map((it) => {
              const meta = STATUS_META[it.status];
              const Icon = meta.Icon;
              return (
                <div key={it.id} className="flex items-center gap-3 p-2 vd-card" data-testid={`burst-item-${it.id}`}>
                  <div className="vd-plate w-12 h-16 shrink-0"><img src={it.preview} alt="" className="w-full h-full object-cover rounded" /></div>
                  <span className="flex-1 min-w-0">
                    {it.data ? (
                      <>
                        <span className="flex items-center gap-2"><WineColorDot color={it.data.color} /><span className="font-title text-lg truncate">{it.data.name} {it.data.year}</span></span>
                        <span className="text-[12px] text-n600 truncate block">{it.data.domaine} · {it.data.format}</span>
                      </>
                    ) : (
                      <span className="text-[13px] text-n500">{it.error || 'En attente…'}</span>
                    )}
                  </span>
                  <span className={`flex items-center gap-1 text-[12px] shrink-0 ${meta.cls}`} data-testid={`burst-status-${it.status}`}>
                    <Icon className={`w-4 h-4 ${meta.spin ? 'animate-spin' : ''}`} strokeWidth={1.8} />{meta.label}
                  </span>
                  {(it.status === 'ready' || it.status === 'toverify') && (
                    <QuantityStepper value={it.qty} min={1} shortcuts={false} testId={`burst-qty-${it.id}`}
                      onChange={(v) => patch(it.id, { qty: v })} />
                  )}
                  {it.status === 'error' && (
                    <button className="text-[12px] text-accent-700 underline" onClick={() => retry(it.id)} data-testid={`burst-retry-${it.id}`}>Réessayer</button>
                  )}
                  <button className="text-n500 hover:text-red-700" onClick={() => remove(it.id)} data-testid={`burst-remove-${it.id}`} title="Retirer"><XCircle className="w-4 h-4" /></button>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex gap-2 mt-4">
          <Button onClick={() => { scanClear(); sync([]); onExit(); }} data-testid="burst-exit">Quitter la rafale</Button>
          <Button variant="primary" disabled={totalBottles === 0} onClick={validate} data-testid="burst-validate">
            Valider {validatable.length} entrée(s)
          </Button>
        </div>
      </div>
    </div>
  );
}
